import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Modal, Box, Button, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { vocabularyApi } from '../../api/global';
import { saveAs } from 'file-saver';
import { Packer } from 'docx';
import { createDocument } from './docx/cvGenerator';
import { createDocumentHWPX } from './hwpx/cvGenerator';
import { createDocumentHWP } from './hwp/cvGenerator';
import HwpIc from '../../assets/images/ic_hwp.svg';
import HwpWhiteIc from '../../assets/images/ic_hwp_white.svg';
import WordIc from '../../assets/images/ic_docx.svg';
import WordWhiteIc from '../../assets/images/ic_docx_white.svg';
import PdfIc from '../../assets/images/ic_pdf.svg';
import PdfWhiteIc from '../../assets/images/ic_pdf_white.svg';
import Mp3Ic from '../../assets/images/ic_mp3.svg';
import Mp3WhiteIc from '../../assets/images/ic_mp3_white.svg';
import Mp3DisableIc from '../../assets/images/ic_mp3_disable.svg';
import TestPaperPDF from './TestPaperPDF_legacy';
import AlertModal from '../common/AlertModal';
import NoticeIC from '../../assets/images/ic_notice_circle.svg';
import { PAPER_TABS } from '../../data/common';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minHeight: '291px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: '40px 40px 30px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const IconBox = ({ icon, iconHover = null, text, onClick, loading = null, disabled = false }) => {
  const [isHover, setIsHover] = useState(null);

  return (
    <button
      type="button"
      className={`relative w-[120px] h-[120px] border border-[#0AAAED] flex flex-col justify-center items-center transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setIsHover(text)}
      onMouseLeave={() => !disabled && setIsHover(null)}
      disabled={disabled}
      style={{
        background: disabled ? 'rgb(226, 232, 239)' : (isHover === text ? '#773DBD' : '#fff'),
        color: disabled ? 'rgb(169, 182, 202)' : (isHover === text ? '#fff' : '#1E293B'),
        borderColor: disabled ? 'rgb(226, 232, 239)' : '#773DBD',
      }}
    >
      <img src={isHover === text ? iconHover : icon} alt="icon" />
      <div>{text}</div>
      <div className="absolute bottom-0 left-0 right-0">
        {loading && <CircularProgress color="primary" size={18} />}
      </div>
    </button>
  );
};
IconBox.propTypes = {
  icon: PropTypes.string,
  iconHover: PropTypes.string,
  iconDisable: PropTypes.string,
  text: PropTypes.string,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

const getFileNameSuffix = (tab) => {
  switch (tab) {
    case PAPER_TABS.TEST_PAPER:
      return '_시험지';
    case PAPER_TABS.ANSWER:
      return '_정답';
    case PAPER_TABS.ANSWER_TEST_PAPER:
      return '_정답표시';
    case PAPER_TABS.VOCABULARY_LIST:
      return '_어휘리스트';
    default:
      return '';
  }
};

function DownloadModal({
  open,
  setOpen,
  config,
  generatedPaper,
  paperTab,
  downloadPdf,
  commonPaperVocaList,
  audioGapAmount,
}) {
  const [loading, setLoading] = useState(false);
  const [alerDisableService, setAlerDisableService] = useState(false);
  const contentRef = useRef();
  const [base64Img, setBase64Img] = useState(null);

  // F타입이 있는지 확인 => 음원 다운로드 비활성화 여부
  const hasFTypeQuestion = useMemo(
  () => {
    const fTypeQuestion = generatedPaper.find(type => type.type === 'F');
    return fTypeQuestion && fTypeQuestion.questions && fTypeQuestion.questions.length > 0;
  }, [generatedPaper]
  );

  // hwp 다운로드
  const downloaHwp = () => {
    // json data : generatedPaper
    // 시험지 정보 : config
    // 시험지 보기 모드(시험지/정답만..) : paperTab
    // 보기 모드 중 '어휘리스트'일 경우 : commonPaperVocaList
    (async () => {
      const hwp = await createDocumentHWP(
        config.orgName,
        config.className,
        config.teacherName,
        config.paperName,
        config.paperDiv,
        config.fontSize,
        config.spacing,
        config.vocaFirstWordShow,
        config.examKorShow,
        config.logoAppearYN === true ? base64Img : null,
        generatedPaper,
        paperTab,
        commonPaperVocaList,
      );
      saveAs(hwp, `${config?.paperName?.replace(/[.\s]/g, '') || ''}${getFileNameSuffix(paperTab)}.hwp`);
    })();
  };

  // hwp 다운로드
  const downloaHwpx = () => {
    // json data : generatedPaper
    // 시험지 정보 : config
    // 시험지 보기 모드(시험지/정답만..) : paperTab
    // 보기 모드 중 '어휘리스트'일 경우 : commonPaperVocaList
    (async () => {
      const hwpx = await createDocumentHWPX(
        config.orgName,
        config.className,
        config.teacherName,
        config.paperName,
        config.paperDiv,
        config.fontSize,
        config.spacing,
        config.vocaFirstWordShow,
        config.examKorShow,
        config.logoAppearYN === true ? base64Img : null,
        generatedPaper,
        paperTab,
        commonPaperVocaList,
      );
      saveAs(
        hwpx,
        `${config?.paperName?.replace(/[.\s]/g, '') || ''}${getFileNameSuffix(paperTab)}.hwpx`,
      );
    })();
  };

  const urlToArrayBuffer = async presignedUrl => {
    try {
      const response = await fetch(presignedUrl, { mode: 'cors' });
      const blob = await response.blob();
      return await blob.arrayBuffer();
    } catch (error) {
      console.error('ArrayBuffer Convert Failed:', error);
      return null;
    }
  };

  // docx 이미지 생성
  useEffect(() => {
    (async () => {
      if (!config?.orgLogoUrl) {
        setBase64Img(null);
        return;
      }
      try {
        const fileKey = config.orgLogoUrl.split('/').pop();
        const response = await vocabularyApi.getSignedUrl(fileKey);
        if (!response?.data?.url) {
          console.error('No Presigned URL');
          return;
        }
        const arrayBuffer = await urlToArrayBuffer(response.data.url);
        const imageBuffer = new Uint8Array(arrayBuffer); // Uint8Array만 react docx의 Image Run에 넣어줄 수 있음.
        if (imageBuffer) {
          console.warn('Docx Image Generated');
          setBase64Img(imageBuffer);
        } else {
          console.error('Docx Image Convert Failed');
          setBase64Img(null);
        }
      } catch (error) {
        console.error('Presigned URL Request Failed', error);
        setBase64Img(null);
      }
    })();
  }, [config.orgLogoUrl]);

  // docx 파일 생성
  const generateDocx = useCallback(() => {
    const doc = createDocument(
      config.orgName,
      config.className,
      config.teacherName,
      config.paperName,
      config.paperDiv,
      config.fontSize,
      config.spacing,
      config.vocaFirstWordShow,
      config.examKorShow,
      base64Img,
      generatedPaper,
      paperTab,
      commonPaperVocaList,
      config.logoAppearYN,
    );
    Packer.toBlob(doc)
      .then(blob => {
        saveAs(blob, `${config?.paperName || ''}${getFileNameSuffix(paperTab)}.docx`);
      })
      .catch(error => {
        console.error('Docx File Generated Failed:', error);
      });
  }, [config, base64Img, generatedPaper, paperTab, commonPaperVocaList]);

  // mp3다운로드
  const downloadMp3 = useCallback(async () => {
    try {
      setLoading(true);
      const apiParams = {
        wordIds: generatedPaper
          .find(type => type.type === 'F')
          .questions.map(word => word.wordId),
        gapSeconds: audioGapAmount,
      };
      const genRes = await vocabularyApi.generateMp3CombinedFile(apiParams, {
        responseType: 'blob',
      });
      console.warn('mp3File generated:', genRes.status);
      if (genRes.data.message === 'Success' && genRes.data.mergedAudioId) {
        const downRes = await vocabularyApi.downloadMp3CombinedFile(
          genRes.data.mergedAudioId,
        );
        console.warn('mp3File downloaded:', downRes.status);
        const blob = new Blob([downRes.data], { type: 'audio/mpeg' });
        saveAs(blob, `${config?.paperName || ''}_음원.mp3`);
      }
      setLoading(false);
    } catch (error) {
      console.error('mp3File download failed:', error);
    } finally {
      setLoading(false);
    }
  }, [generatedPaper, audioGapAmount, config]);

  return (
    <Modal open={open} onClose={() => setOpen(null)}>
      <Box sx={style}>
        <div className="w-full flex items-center justify-between text-[14px] gap-[40px] font-[400]">
          <div className="flex flex-col justify-start items-center">
            <div className="text-[#A9B6CA] font-[400] text-[14px] mb-[16px]">
              문서 다운로드
            </div>
            <div className="flex items-center gap-2">
              <IconBox
                // icon={HwpDisabledIc}
                icon={HwpIc}
                iconHover={HwpWhiteIc}
                text=".hwp"
                onClick={downloaHwp}
              />
              <IconBox
                icon={HwpIc}
                iconHover={HwpWhiteIc}
                text=".hwpx"
                onClick={downloaHwpx}
              />
              <IconBox
                icon={WordIc}
                iconHover={WordWhiteIc}
                text=".docx"
                onClick={generateDocx}
              />
              <IconBox
                icon={PdfIc}
                iconHover={PdfWhiteIc}
                text=".pdf"
                onClick={downloadPdf}
              />
            </div>
          </div>
          <div className="flex flex-col justify-start items-center">
            <div className="text-[#A9B6CA] font-[400] text-[14px] mb-[16px]">
              음원 다운로드
            </div>
            <IconBox
              icon={hasFTypeQuestion ? Mp3Ic : Mp3DisableIc}
              iconHover={Mp3WhiteIc}
              text=".mp3"
              onClick={downloadMp3}
              loading={loading}
              disabled={!hasFTypeQuestion}
            />
          </div>
        </div>
        <div className="w-full mt-[16px] mb-[33px] bg-[#F6F9FF] p-[20px]">
          <div className="flex items-center gap-2 mb-[5px]">
            <img
              src={NoticeIC}
              alt="notice"
              className="w-[20px] h-[20px] inline-block"
            />
            <div className="font-[14px] text-[#773DBD] font-[700]">
              HWPX 호환성 관련 안내
            </div>
          </div>
          <div className="pl-[25px]">
            <div className="flex gap-[10px] text-[#1E293B] text-[12px] font-[400] mb-[2px]">
              <div>•</div>
              <div>
                2018년 이후 버전은 별도 조치 없이 정상적으로 사용할 수 있습니다.
              </div>
            </div>
            <div className="flex gap-[10px] text-[#1E293B] text-[12px] font-[400] mb-[2px]">
              <div>•</div>
              <div>
                2010~2018년 버전은 별도의 패치 파일 설치가 필요합니다.
                <br />
                해당 버전 사용자께서는{' '}
                <a
                  href="https://www.hancom.com/support/downloadCenter/download"
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'underline' }}
                >
                  한컴 다운로드 센터
                </a>
                에서 패치 파일을 다운로드한 후 실행해 주세요.
              </div>
            </div>
            <div className="flex gap-[10px] text-[#1E293B] text-[12px] font-[400] mb-[2px]">
              <div>•</div>
              <div>
                본 제품은 한글과컴퓨터의 HWP 문서 파일(.hwp,.hwpx) 공개 문서를
                참고하여 개발하였습니다.
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center justify-center gap-2">
          <Button
            variant="dark"
            onClick={() => {
              setOpen(false);
            }}
            sx={{ width: '113px' }}
          >
            닫기
          </Button>
        </div>
        <Box sx={{ display: 'none' }}>
          <TestPaperPDF
            tab={paperTab}
            config={config}
            generatedPaper={generatedPaper}
            contentRef={contentRef}
          />
        </Box>
        <AlertModal
          open={alerDisableService}
          setOpen={setAlerDisableService}
          content="서비스 준비중입니다."
          confirmLabel="확인"
          onConfirm={() => setAlerDisableService(false)}
        />
      </Box>
    </Modal>
  );
}

DownloadModal.propTypes = {
  paperTab: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  config: PropTypes.object,
  generatedPaper: PropTypes.array,
  startNumbers: PropTypes.array,
  downloadPdf: PropTypes.func.isRequired,
  commonPaperVocaList: PropTypes.array,
  audioGapAmount: PropTypes.number.isRequired,
};

export default React.memo(DownloadModal);
